import { CalendarDate, parseDate } from '@internationalized/date';
import { Spacing } from '@persona-id/design-tokens';
import { MoreIcon } from '@persona-id/design-tokens/icons';
import { omit } from 'lodash/fp';
import { ComponentProps, KeyboardEvent, KeyboardEventHandler, useCallback, useState } from 'react';
import styled from 'styled-components';

import ReactSelect from 'dashboard/components/ReactSelect';
import { Inquiry } from 'dashboard/models/inquiry';
import Button from 'design-system/components/Button';
import DateField from 'design-system/components/DatePicker/DateField';
import DropdownMenu from 'design-system/components/DropdownMenu';
import { Input } from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import Sticker from 'design-system/components/Sticker';
import { Label, Paragraph } from 'design-system/components/Text';
import TextArea from 'design-system/components/TextArea';
import ToggleSlider from 'design-system/components/ToggleSlider';
import { paragraphMixins } from 'design-system/mixins/text';
import {
  FieldSchema,
  FieldSchemaChoices,
  FieldSchemaMultiChoices,
  FieldSchemaType,
  FieldValue,
} from 'lib/fields';
import useToggle from 'lib/hooks/useToggle';
import { assertNever } from 'lib/utils/types';

const StyledLabel = styled(Label).attrs(() => ({ size: 'sm' }))`
  font-weight: ${({ theme }) => theme.text.paragraph.mdBold.fontWeight};
  display: inline-block;
  margin-bottom: 0px;
`;

const StyledSelectInput = styled(ReactSelect).attrs(() => ({ size: 'sm' }))`
  margin-bottom: ${Spacing[2]};
`;

const StyledInput = styled(Input).attrs(() => ({ size: 'md' }))`
  margin-bottom: ${Spacing[2]};
`;

const StyledTextAreaInput = styled(TextArea).attrs(() => ({ size: 'md' }))`
  margin-bottom: ${Spacing[2]};
`;

const StyledDateFieldInput = styled(DateField)``;
const DateFieldWrapped = (props: ComponentProps<typeof StyledDateFieldInput>) => {
  // Hack around not being able to style margin bottom for `DateField`
  return (
    <>
      <StyledDateFieldInput {...props} />
      <Spacer height={1} />
    </>
  );
};

const StyledMessage = styled(Paragraph).attrs(() => ({ size: 'md' }))`
  padding-top: ${Spacing[1]};
  padding-bottom: ${Spacing[3]};
  color: ${({ theme }) => theme.color.text.secondary};
  font-size: ${({ theme }) => theme.text.paragraph.mdBold.fontSize};
`;

const StyledToggleInput = styled(ToggleSlider).attrs(() => ({ size: 'sm' }))`
  margin-bottom: ${Spacing[2]};
`;

const StyledFieldValue = styled.div`
  ${paragraphMixins.md}
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: ${Spacing[2]};
`;

const StyledFieldValuePlaceholder = styled.div`
  ${paragraphMixins.md}
  color: ${({ theme }) => theme.color.text.disabled};
  margin-bottom: ${Spacing[2]};
`;

const MultiChoiceResultContainer = styled.div`
  display: flex;
  gap: ${Spacing[2]};
  flex-wrap: wrap;
  margin-bottom: ${Spacing[2]};
`;

const FieldLabelContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing[2]};
  height: 20px;
`;

const FieldStubContainer = styled.div`
  display: flex;
  width: 100%;
  gap: ${Spacing[2]};
  flex-direction: row;
  justify-content: space-between;
  height: ${Spacing[5]};
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${Spacing[2]};
  margin-bottom: ${Spacing[3]};
`;

const SHOWN_FIELD_TYPES = [
  FieldSchemaType.String,
  FieldSchemaType.Date,
  FieldSchemaType.Choices,
  FieldSchemaType.MultiChoices,
  FieldSchemaType.Boolean,
  FieldSchemaType.Number,
  FieldSchemaType.Integer,
  FieldSchemaType.Hash,
  FieldSchemaType.Json,
];

interface FieldStubInputProps {
  fieldNameKebabCase: string;
  fieldSchema: FieldSchema;
  inputValue: FieldValue;
  setInputValue: (value: FieldValue) => void;
  handleKeyDown: (e: KeyboardEvent) => void;
}

const FieldStubInput = ({
  fieldNameKebabCase,
  fieldSchema,
  inputValue,
  setInputValue,
  handleKeyDown,
}: FieldStubInputProps) => {
  const commonProps = {
    id: `fields-stubs-${fieldNameKebabCase}`,
    onKeyDown: handleKeyDown,
    autoFocus: true,
  };

  switch (fieldSchema.type) {
    case FieldSchemaType.String:
      return (
        <StyledInput
          {...commonProps}
          value={inputValue as string}
          onChange={(e) => setInputValue(e.target.value)}
        />
      );
    case FieldSchemaType.Date:
      return (
        <DateFieldWrapped
          {...commonProps}
          value={inputValue && parseDate(inputValue as string)}
          onChange={(value: CalendarDate) => setInputValue(value && value.toString())}
        />
      );
    case FieldSchemaType.Choices:
      return (
        <StyledSelectInput
          {...commonProps}
          value={inputValue}
          onChange={setInputValue}
          options={((fieldSchema as FieldSchemaChoices).config.options || []).map((option) => ({
            value: option,
            label: option,
          }))}
        />
      );
    case FieldSchemaType.MultiChoices:
      return (
        <StyledSelectInput
          {...commonProps}
          value={inputValue}
          onChange={setInputValue}
          options={((fieldSchema as FieldSchemaMultiChoices).config.options || []).map(
            (option) => ({
              value: option,
              label: option,
            }),
          )}
          isMulti
        />
      );
    case FieldSchemaType.Boolean:
      return (
        <StyledToggleInput
          {...commonProps}
          labelOn='True'
          labelOff='False'
          value={inputValue as boolean}
          onChange={setInputValue}
        />
      );
    case FieldSchemaType.Number:
      return (
        <StyledInput
          {...commonProps}
          type='number'
          // This will allow floats
          step='any'
          onChange={(e) => {
            const value = e.target.value;
            setInputValue(value === '' ? null : parseFloat(value));
          }}
          value={(inputValue && inputValue.toString()) as string}
        />
      );
    case FieldSchemaType.Integer:
      return (
        <StyledInput
          {...commonProps}
          type='number'
          onChange={(e) => {
            const value = e.target.value;
            setInputValue(value === '' ? null : parseInt(value));
          }}
          value={(inputValue && inputValue.toString()) as string}
        />
      );
    case FieldSchemaType.Hash:
    case FieldSchemaType.Json:
      return (
        <StyledTextAreaInput
          {...commonProps}
          value={typeof inputValue === 'string' ? inputValue : JSON.stringify(inputValue)}
          onChange={(e) => {
            try {
              setInputValue(JSON.parse(e.target.value));
            } catch (exception) {
              setInputValue(e.target.value);
            }
          }}
        />
      );
    default:
      return <></>;
  }
};

interface ShownFieldValueProps {
  fieldSchema: FieldSchema;
  fieldValue: FieldValue;
}

const ShownFieldValue = ({ fieldSchema, fieldValue }: ShownFieldValueProps) => {
  switch (fieldSchema.type) {
    case FieldSchemaType.String:
    case FieldSchemaType.Date:
    case FieldSchemaType.Choices:
    case FieldSchemaType.Number:
    case FieldSchemaType.Integer:
      return <StyledFieldValue>{String(fieldValue)}</StyledFieldValue>;
    case FieldSchemaType.Hash:
    case FieldSchemaType.Json:
      return (
        <StyledFieldValue>
          {typeof fieldValue === 'string' ? fieldValue : JSON.stringify(fieldValue)}
        </StyledFieldValue>
      );
    case FieldSchemaType.MultiChoices:
      return (fieldValue as string[]).length > 0 ? (
        <MultiChoiceResultContainer>
          {(fieldValue as string[]).map((value, idx) => {
            return (
              <Sticker key={idx} variant='basic'>
                {value}
              </Sticker>
            );
          })}
        </MultiChoiceResultContainer>
      ) : (
        <StyledFieldValuePlaceholder>Empty</StyledFieldValuePlaceholder>
      );
    case FieldSchemaType.Boolean:
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      return <StyledFieldValue>{fieldValue.toString()}</StyledFieldValue>;
    default:
      return assertNever(fieldSchema.type as never);
  }
};

interface FieldStubEditorProps {
  fieldNameKebabCase: string;
  fieldSchema: FieldSchema;
  setFieldStubValue: (value: FieldValue) => void;
  fieldStubValue: FieldValue;
  closeEdit: () => void;
}

const FieldStubEditor = ({
  fieldNameKebabCase,
  fieldSchema,
  setFieldStubValue,
  fieldStubValue,
  closeEdit,
}: FieldStubEditorProps) => {
  const [inputValue, setInputValue] = useState(fieldStubValue);
  const saveStub = useCallback(() => {
    const savedValue = inputValue === '' ? null : inputValue;
    setFieldStubValue(savedValue);
    closeEdit();
  }, [closeEdit, inputValue, setFieldStubValue]);
  const handleKeyDown: KeyboardEventHandler = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        saveStub();
      }
    },
    [saveStub],
  );

  return (
    <>
      <FieldStubInput
        fieldNameKebabCase={fieldNameKebabCase}
        fieldSchema={fieldSchema}
        handleKeyDown={handleKeyDown}
        inputValue={inputValue}
        setInputValue={setInputValue}
      />
      <ButtonsContainer>
        <Button onClick={saveStub} size='sm' width='auto'>
          Save
        </Button>
        <Button variant='basic' onClick={closeEdit} size='sm' width='auto'>
          Cancel
        </Button>
      </ButtonsContainer>
    </>
  );
};

interface DisplayedFieldProps {
  fieldNameKebabCase: string;
  fieldStubValue: FieldValue;
  setFieldStubValue: (value: FieldValue) => void;
  fieldValue: FieldValue;
  fieldSchema: FieldSchema;
  showStubbedValue: boolean;
  closeEdit: () => void;
  isEditing: boolean;
}

const DisplayedField = ({
  fieldNameKebabCase,
  fieldStubValue,
  setFieldStubValue,
  fieldValue,
  fieldSchema,
  showStubbedValue,
  closeEdit,
  isEditing,
}: DisplayedFieldProps) => {
  if (isEditing) {
    return (
      <FieldStubEditor
        fieldNameKebabCase={fieldNameKebabCase}
        fieldSchema={fieldSchema}
        fieldStubValue={fieldStubValue}
        setFieldStubValue={setFieldStubValue}
        closeEdit={closeEdit}
      />
    );
  }

  const shownValue = showStubbedValue ? fieldStubValue : fieldValue;

  if (shownValue != null) {
    return <ShownFieldValue fieldSchema={fieldSchema} fieldValue={shownValue} />;
  }
  return <StyledFieldValuePlaceholder>Empty</StyledFieldValuePlaceholder>;
};

type FieldEntryProps = {
  fieldName: string;
  fieldValue: FieldValue;
  fieldSchema: FieldSchema;
  fieldsStubs: Record<string, FieldValue>;
  setFieldsStubs: (fieldsStubs: Record<string, FieldValue>) => void;
};

const FieldEntry = ({
  fieldName,
  fieldValue,
  fieldSchema,
  fieldsStubs,
  setFieldsStubs,
}: FieldEntryProps) => {
  const fieldNameKebabCase = fieldName.replace(/_/g, '-');

  const [isEditingFieldsStub, { enable: enableEditFieldsStub, disable: hideEditFieldsStub }] =
    useToggle(false);

  return (
    <div role='listitem'>
      <FieldStubContainer key={fieldNameKebabCase}>
        <FieldLabelContainer>
          <StyledLabel htmlFor={`fields-stubs-${fieldNameKebabCase}`}>{fieldName}</StyledLabel>
          {fieldNameKebabCase in fieldsStubs && <Sticker variant='basic'>Simulated</Sticker>}
        </FieldLabelContainer>
        {!isEditingFieldsStub && (
          <DropdownMenu
            trigger={
              <Button
                id={`inquiry-template-simulate-fields-option-${fieldNameKebabCase}__dropdown`}
                aria-label={`Options for field ${fieldName}`}
                size='sm'
                variant='inverse'
                width='icon'
              >
                <MoreIcon />
              </Button>
            }
          >
            <DropdownMenu.Item onClick={enableEditFieldsStub}>Simulate Value</DropdownMenu.Item>
            <DropdownMenu.Item
              onClick={() => setFieldsStubs(omit([fieldNameKebabCase], fieldsStubs))}
              disabled={!(fieldNameKebabCase in fieldsStubs)}
            >
              Unset Simulated Value
            </DropdownMenu.Item>
          </DropdownMenu>
        )}
      </FieldStubContainer>
      <DisplayedField
        fieldNameKebabCase={fieldNameKebabCase}
        fieldValue={fieldValue}
        fieldStubValue={fieldsStubs[fieldNameKebabCase]}
        setFieldStubValue={(newValue: FieldValue) =>
          setFieldsStubs({ ...fieldsStubs, [fieldNameKebabCase]: newValue })
        }
        isEditing={isEditingFieldsStub}
        fieldSchema={fieldSchema}
        showStubbedValue={fieldNameKebabCase in fieldsStubs}
        closeEdit={hideEditFieldsStub}
      />
    </div>
  );
};

interface InquiryFieldsConfigProps {
  inquiry: Inquiry | undefined;
  fieldSchemas: FieldSchema[];
  fieldsStubs: Record<string, FieldValue>;
  setFieldsStubs: (fieldsStubs: Record<string, FieldValue>) => void;
}

export const InquiryTemplateSimulateFieldsConfigTab = ({
  inquiry,
  fieldSchemas,
  fieldsStubs,
  setFieldsStubs,
}: InquiryFieldsConfigProps) => {
  if (inquiry == null) {
    return <></>;
  }

  if (fieldSchemas.length === 0) {
    return <StyledMessage>No fields in this Inquiry.</StyledMessage>;
  }

  return (
    <>
      {fieldSchemas.map((fieldSchema) => {
        const fieldKey = fieldSchema.key;
        return SHOWN_FIELD_TYPES.includes(fieldSchema.type) ? (
          <FieldEntry
            fieldName={fieldKey}
            // We specifically CANNOT use lodash kebabCase here, because kebabCase will put hyphens between
            // consecutive alpha-numeric and numeric-alpha characters. Instead, simply replace underscores
            // with hyphens
            fieldValue={inquiry.attributes.fields[fieldKey.replace(/_/g, '-')].value}
            fieldSchema={fieldSchema}
            fieldsStubs={fieldsStubs}
            setFieldsStubs={setFieldsStubs}
          />
        ) : (
          <></>
        );
      })}
    </>
  );
};
